import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  Inject,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

export interface TocSection {
  id: string;
  label: string;
  active: boolean;
}

@Component({
    selector: 'app-refund-policy',
    imports: [CommonModule, RouterModule],
    templateUrl: './refund-policy.component.html',
    styleUrl: './refund-policy.component.css'
})
export class RefundPolicyComponent implements OnInit, AfterViewInit, OnDestroy {
  lastUpdated = '5th March 2026';

  tocSections: TocSection[] = [
    { id: 'r1', label: '1. Introduction', active: false },
    { id: 'r2', label: '2. General No-Refund Rule', active: false },
    { id: 'r3', label: '3. Credit Consideration', active: false },
    { id: 'r4', label: '4. LLM Uncertainty & Hallucination', active: false },
    { id: 'r5', label: '5. What Credits Cover', active: false },
    { id: 'r6', label: '6. Subscription Cancellation', active: false },
    { id: 'r7', label: '7. Billing Errors', active: false },
    { id: 'r8', label: '8. Ineligible Situations', active: false },
    { id: 'r9', label: '9. How to Request', active: false },
    { id: 'r10', label: '10. Review Process', active: false },
    { id: 'r11', label: '11. Chargebacks', active: false },
    { id: 'r12', label: '12. Beta Platform', active: false },
    { id: 'r13', label: '13. Governing Law', active: false },
    { id: 'r14', label: '14. Changes to Policy', active: false },
    { id: 'r15', label: '15. Contact', active: false },
  ];

  private intersectionObserver?: IntersectionObserver;
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private meta: Meta,
    private titleService: Title,
    private renderer: Renderer2,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.setMetaTags();
    this.applySecurityHeaders();
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) this.initScrollSpy();
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  private setMetaTags(): void {
    const title = 'Refund Policy — Angularize.dev by Pythrust Technologies';
    this.titleService.setTitle(title);
    const desc =
      'Angularize.dev Refund Policy — understand our no-refund stance, when platform credits may be considered, LLM limitations, and how to raise a billing concern.';
    this.meta.updateTag({ name: 'description', content: desc });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: desc });
    this.meta.updateTag({
      property: 'og:url',
      content: 'https://angularize.dev/refund-policy',
    });
    if (this.isBrowser)
      this.ensureCanonical('https://angularize.dev/refund-policy');
  }

  private ensureCanonical(url: string): void {
    let link = document.querySelector(
      "link[rel='canonical']",
    ) as HTMLLinkElement;
    if (!link) {
      link = this.renderer.createElement('link') as HTMLLinkElement;
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.appendChild(document.head, link);
    }
    this.renderer.setAttribute(link, 'href', url);
  }

  private applySecurityHeaders(): void {
    if (!this.isBrowser) return;
    this.meta.updateTag({
      name: 'referrer',
      content: 'strict-origin-when-cross-origin',
    });
    this.meta.updateTag({
      'http-equiv': 'X-Content-Type-Options',
      content: 'nosniff',
    });
    this.meta.updateTag({ 'http-equiv': 'X-Frame-Options', content: 'DENY' });
    this.meta.updateTag({
      'http-equiv': 'Content-Security-Policy',
      content: [
        "default-src 'self'",
        "script-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
        "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://fonts.gstatic.com",
        "font-src 'self' https://fonts.gstatic.com",
        "img-src 'self' data: https:",
        "connect-src 'self'",
        "frame-ancestors 'none'",
        "base-uri 'self'",
        "form-action 'self'",
      ].join('; '),
    });
  }

  private initScrollSpy(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.tocSections.forEach(
              (s) => (s.active = s.id === entry.target.id),
            );
          }
        });
      },
      { rootMargin: '-15% 0px -80% 0px' },
    );
    this.tocSections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) this.intersectionObserver!.observe(el);
    });
  }

  scrollTo(id: string): void {
    if (!this.isBrowser) return;
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.isBrowser) window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
